import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGetLeagueFixtures } from '../api/fixturesApi';
import { SignupGateModal } from '../components/auth/SignupGateModal';
import { Spinner } from '../components/common/Spinner';
import { useAuth } from '../hooks/useAuth';
import { useCurrentTeamId } from '../hooks/useCurrentTeamId';
import { useLiveScore } from '../hooks/useLiveScore';
import { useQuickplayRematchNavigation } from '../hooks/useQuickplayRematchNavigation';

const ACTIVE_MATCH_STATUSES = new Set([
    'waitingforready',
    'waitingforengine',
    'inprogress'
]);

const COMPLETED_MATCH_STATUSES = new Set(['completed', 'forfeited']);

// A quickplay league only grows by one match per rematch, so one generous page
// covers the realistic history size; deriving the limit from cached league data
// proved stale after a rematch.
const MATCH_HISTORY_PAGE_SIZE = 200;

// The global query staleTime (5 min, no refetch on focus) left this page
// showing a finished match as Live until a manual reload. Always refetch on
// mount, and keep polling while a match is running so its status converges
// even if the backend marks it completed a few seconds after full time.
const LIVE_STATUS_POLL_MS = 5000;

const FIXTURES_QUERY_OPTIONS = {
    refetchInterval: (query) => (
        (query?.state?.data?.matches || []).some(isActiveMatch) ? LIVE_STATUS_POLL_MS : false
    ),
    refetchOnMount: 'always',
    staleTime: 0
};

function getEntityId(entity) {
    if (!entity) return null;
    if (typeof entity === 'string') return entity;
    return entity._id ?? entity.id ?? null;
}

function normalizeStatus(status) {
    return String(status || '').trim().toLowerCase().replace(/_/g, '');
}

function isActiveMatch(match) {
    return ACTIVE_MATCH_STATUSES.has(normalizeStatus(match?.status));
}

function getStatusLabel(match) {
    const status = normalizeStatus(match?.status);
    if (ACTIVE_MATCH_STATUSES.has(status)) return 'Live';
    if (COMPLETED_MATCH_STATUSES.has(status)) return 'Completed';
    if (status === 'engineerror' || status === 'cancelled') return 'Error';
    return 'Scheduled';
}

function getMatchPerspective(match, currentTeamId) {
    const homeTeamId = getEntityId(match?.homeTeam);
    const isHomeTeam = currentTeamId
        ? String(homeTeamId) === String(currentTeamId)
        : true;
    const userScore = isHomeTeam ? match?.homeScore : match?.awayScore;
    const opponentScore = isHomeTeam ? match?.awayScore : match?.homeScore;

    return {
        isHomeTeam,
        opponent: isHomeTeam ? match?.awayTeam : match?.homeTeam,
        opponentScore: Number(opponentScore ?? 0),
        userScore: Number(userScore ?? 0)
    };
}

function getCompletedResult(match, score) {
    if (normalizeStatus(match?.status) !== 'completed') {
        return null;
    }
    if (score.userScore > score.opponentScore) return 'win';
    if (score.userScore < score.opponentScore) return 'loss';
    return 'draw';
}

function formatMatchDate(dateValue) {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Date unavailable';
    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
}

function getMatchRowDestination(match, leagueSlug, currentTeamId) {
    const status = normalizeStatus(match?.status);
    // Completed matches open straight on the post-match summary; anything still
    // running (or waiting) opens the live match view.
    if (status === 'completed' && currentTeamId) {
        return `/${leagueSlug}/match/${match._id}/summary/${currentTeamId}`;
    }
    if (ACTIVE_MATCH_STATUSES.has(status)) {
        return `/${leagueSlug}/match/${match._id}/match`;
    }
    return `/${leagueSlug}/match/${match._id}/lineups`;
}

function QuickplayMatchRow({ currentTeamId, leagueSlug, match }) {
    const status = normalizeStatus(match?.status);
    const isFinished = COMPLETED_MATCH_STATUSES.has(status) || status === 'engineerror';
    const { liveScore } = useLiveScore(match?._id, { isMatchFinished: isFinished });
    const matchWithScore = liveScore
        ? { ...match, homeScore: liveScore.home, awayScore: liveScore.away }
        : match;
    const perspective = getMatchPerspective(matchWithScore, currentTeamId);
    const result = getCompletedResult(matchWithScore, perspective);
    const resultLabel = result ? result.charAt(0).toUpperCase() : null;

    return (
        <Link
            className={`quickplay-history-row${result ? ` result-${result}` : ''}`}
            to={getMatchRowDestination(match, leagueSlug, currentTeamId)}
        >
            <span className="quickplay-result-accent" aria-hidden="true" />
            <span className="quickplay-history-opponent">
                <small>Opponent</small>
                <strong>{perspective.opponent?.name || 'Unknown opponent'}</strong>
                <span>{formatMatchDate(match.date)}</span>
            </span>
            <span className="quickplay-history-score">
                <strong>{perspective.userScore} - {perspective.opponentScore}</strong>
                {resultLabel ? <span aria-label={`Result: ${result}`}>{resultLabel}</span> : null}
            </span>
            <span className={`quickplay-history-status status-${getStatusLabel(match).toLowerCase()}`}>
                {getStatusLabel(match)}
            </span>
            <span className="ico icon-arrow-right" aria-hidden="true" />
        </Link>
    );
}

function QuickplayMatchHistoryPage({ league }) {
    const location = useLocation();
    const { isGuest: authIsGuest, user } = useAuth();
    const currentTeamId = useCurrentTeamId();
    const [showGuestBanner, setShowGuestBanner] = useState(true);
    const [showSignupGate, setShowSignupGate] = useState(false);
    const isGuest = authIsGuest === true || user?.isGuest === true;
    const {
        data: fixturesData,
        error: fixturesError,
        isLoading
    } = useGetLeagueFixtures(league?._id, 1, MATCH_HISTORY_PAGE_SIZE, undefined, FIXTURES_QUERY_OPTIONS);
    const {
        error: rematchError,
        isStartingRematch,
        startRematch
    } = useQuickplayRematchNavigation();
    const matches = useMemo(() => (
        [...(fixturesData?.matches || [])].sort((first, second) => (
            new Date(second.date).getTime() - new Date(first.date).getTime()
        ))
    ), [fixturesData?.matches]);
    const liveMatch = matches.find(isActiveMatch);

    return (
        <main className="quickplay-history-page">
            <div className="wrapper">
                <section className="quickplay-history-card" aria-labelledby="quickplay-history-title">
                    <div className="quickplay-history-heading">
                        <div>
                            <p className="play-kicker">Quick Play</p>
                            <h1 id="quickplay-history-title">Your matches</h1>
                            <p>Every result, all in one place.</p>
                        </div>
                        {liveMatch ? (
                            <Link
                                className="submit-btn"
                                to={`/${league.slug}/match/${liveMatch._id}/match`}
                            >
                                Back to live match
                            </Link>
                        ) : (
                            <button
                                type="button"
                                className="submit-btn"
                                disabled={isStartingRematch}
                                onClick={startRematch}
                            >
                                {isStartingRematch ? 'Starting...' : 'New match'}
                            </button>
                        )}
                    </div>

                    {isGuest && showGuestBanner ? (
                        <aside className="quickplay-guest-warning" aria-label="Guest account warning">
                            <p>
                                You are playing as a guest. If you close your browser you can lose your match history. Sign up to keep it.
                            </p>
                            <div>
                                <button
                                    type="button"
                                    className="submit-btn"
                                    onClick={() => setShowSignupGate(true)}
                                >
                                    Sign up to keep it
                                </button>
                                <button
                                    type="button"
                                    className="quickplay-guest-dismiss"
                                    aria-label="Dismiss guest warning"
                                    onClick={() => setShowGuestBanner(false)}
                                >
                                    Dismiss
                                </button>
                            </div>
                        </aside>
                    ) : null}

                    {rematchError ? <p className="error-message" role="alert">{rematchError}</p> : null}

                    {isLoading ? (
                        <div className="quickplay-history-loading">
                            <Spinner />
                            <p>Loading your matches...</p>
                        </div>
                    ) : fixturesError ? (
                        <p className="error-message" role="alert">
                            Your matches could not be loaded. Please refresh and try again.
                        </p>
                    ) : matches.length === 0 ? (
                        <div className="quickplay-history-empty">
                            <h2>No matches yet</h2>
                            <p>Start a new match to build your history.</p>
                        </div>
                    ) : (
                        <div className="quickplay-history-list">
                            {matches.map(match => (
                                <QuickplayMatchRow
                                    currentTeamId={currentTeamId}
                                    key={match._id}
                                    leagueSlug={league.slug}
                                    match={match}
                                />
                            ))}
                        </div>
                    )}
                </section>
            </div>

            <SignupGateModal
                description="Create your free account to keep your Quick Play team and match history."
                onDismiss={() => setShowSignupGate(false)}
                open={showSignupGate}
                redirectTo={`${location.pathname}${location.search}`}
                title="Keep your match history"
            />
        </main>
    );
}

export { QuickplayMatchHistoryPage };
